import { useEffect, useRef } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@buildex/ui';
import { ColorInput } from '@mantine/core';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { BRANDING_PREVIEW_MESSAGE } from '@buildex/core';
import type { TenantBranding } from '@buildex/module-contract';
import type { AdminTenant } from '@/entities/tenant/types';
import {
  $brandingDraft,
  $brandingSaved,
  $brandingSaving,
  brandingSaveSubmitted,
  draftChanged,
  draftInitialized,
  draftReset,
} from '@/features/edit-branding/model';
import { CABINET_ORIGIN } from '@/shared/env';

const RADIUS_OPTIONS = ['xs', 'sm', 'md', 'lg', 'xl'];
const FONT_OPTIONS = [
  { value: '', label: 'Системный' },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: 'Georgia, serif', label: 'Georgia' },
];

interface BrandingEditorProps {
  tenant: AdminTenant;
}

/** Редактор брендинга с live-превью кабинета в iframe (§8) */
export function BrandingEditor({ tenant }: BrandingEditorProps) {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { draft, saving, saved, onChange, onSubmit, onReset } = useUnit({
    draft: $brandingDraft,
    saving: $brandingSaving,
    saved: $brandingSaved,
    onChange: draftChanged,
    onSubmit: brandingSaveSubmitted,
    onReset: draftReset,
  });

  // Инициализация черновика при открытии тенанта
  useEffect(() => {
    draftInitialized(tenant.branding);
  }, [tenant.id, tenant.branding]);

  // Каждое изменение черновика → postMessage в iframe кабинета
  useEffect(() => {
    if (draft === null) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: BRANDING_PREVIEW_MESSAGE, payload: draft satisfies TenantBranding },
      CABINET_ORIGIN,
    );
  }, [draft]);

  if (draft === null) return null;

  const isDirty = JSON.stringify(draft) !== JSON.stringify(tenant.branding);

  return (
    <Grid gutter="md">
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Card>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={650}>{t('branding.title')}</Text>
              {saved ? <Badge color="teal">{t('branding.saved')}</Badge> : null}
            </Group>

            <ColorInput
              label={t('branding.primaryColor')}
              description={t('branding.primaryColorHint')}
              value={draft.primaryColor}
              onChange={(primaryColor) => onChange({ primaryColor })}
            />
            <TextInput
              label={t('branding.logoUrl')}
              description={t('branding.logoUrlHint')}
              placeholder="https://…/logo.svg"
              value={draft.logoUrl}
              onChange={(e) => onChange({ logoUrl: e.currentTarget.value })}
            />
            <Select
              label={t('branding.radius')}
              data={RADIUS_OPTIONS}
              value={draft.radius ?? 'md'}
              onChange={(radius) =>
                radius && onChange({ radius: radius as TenantBranding['radius'] })
              }
            />
            <Select
              label={t('branding.font')}
              data={FONT_OPTIONS}
              value={draft.fontFamily ?? ''}
              onChange={(fontFamily) =>
                onChange({ fontFamily: fontFamily === '' || fontFamily === null ? undefined : fontFamily })
              }
            />

            <Group justify="space-between" mt="sm">
              <Button
                variant="default"
                disabled={!isDirty || saving}
                onClick={() => onReset(tenant.branding)}
              >
                {t('branding.reset')}
              </Button>
              <Button
                loading={saving}
                disabled={!isDirty}
                onClick={() => onSubmit({ tenantId: tenant.id, branding: draft })}
              >
                {t('branding.save')}
              </Button>
            </Group>
          </Stack>
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 8 }}>
        <Card p="xs" h="100%">
          <Stack gap="xs" h="100%">
            <Group justify="space-between" px="xs">
              <Text size="sm" fw={600}>
                {t('branding.preview')}
              </Text>
              <Text size="xs" c="dimmed">
                {CABINET_ORIGIN}
              </Text>
            </Group>
            <iframe
              ref={iframeRef}
              src={`${CABINET_ORIGIN}/?previewMode=1`}
              title={t('branding.preview')}
              style={{
                border: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 'var(--mantine-radius-md)',
                width: '100%',
                minHeight: 520,
                flex: 1,
              }}
            />
            <Alert variant="light" p="xs">
              <Text size="xs">{t('branding.previewHint')}</Text>
            </Alert>
          </Stack>
        </Card>
      </Grid.Col>
    </Grid>
  );
}
